// ============================================================================
//  PRECIO REAL DE UN SERVICIO DENTRO DE UNA CITA
//
//  Esta lógica vivía solo dentro de Appointments.tsx. Los demás módulos
//  (Dashboard, Comisiones, exports de ingresos) leían `serviceDiscounts` a
//  secas y por eso mostraban el precio de lista cuando el descuento no estaba
//  guardado en la cita — el caso típico es un paquete de sesiones comprado
//  DESPUÉS de agendar. Al tenerla en un solo archivo, todos los módulos dan
//  el mismo número y deja de desincronizarse.
// ============================================================================

export interface PricingDeps {
  services: any[];
  getPatient: (id: string) => any;
  /** Descuento del paquete de sesiones activo, si lo hay. */
  getActivePackageDiscount: (
  patientId: string,
  serviceId: string)
  => number | undefined;
  /** Oferta general vigente para ese servicio en esa fecha. */
  getActiveOfferForService: (serviceId: string, date: string) => any;
}

export interface OfferInfo {
  offer: any | undefined;
  isPackageDiscount: boolean;
  discountPercent: number | undefined;
  discountAmount: number;
  finalPrice: number;
}

/**
 * Precio de un servicio para una fecha y paciente dados, SIN considerar una
 * cita ya existente. El paquete de sesiones tiene prioridad sobre la oferta
 * general.
 */
export function getServiceOfferInfo(
deps: PricingDeps,
serviceId: string,
date: string,
patientId?: string)
: OfferInfo {
  const service = deps.services.find((s: any) => s.id === serviceId);
  const price = service?.price || 0;

  const packageDiscountPercent = patientId ?
  deps.getActivePackageDiscount(patientId, serviceId) :
  undefined;

  if (packageDiscountPercent !== undefined) {
    const discountAmount = price * (packageDiscountPercent / 100);
    return {
      offer: undefined,
      isPackageDiscount: true,
      discountPercent: packageDiscountPercent,
      discountAmount,
      finalPrice: Math.max(0, price - discountAmount)
    };
  }

  const offer = deps.getActiveOfferForService(serviceId, date);
  const discountAmount = offer ? price * (offer.discountPercent / 100) : 0;
  return {
    offer,
    isPackageDiscount: false,
    discountPercent: offer?.discountPercent,
    discountAmount,
    finalPrice: Math.max(0, price - discountAmount)
  };
}

/**
 * Precio de un servicio dentro de una CITA YA EXISTENTE.
 *
 * Orden de prioridad:
 *   1. Excepción manual `<serviceId>__override` en service_discounts.
 *   2. Descuento congelado (paquete de sesiones o certificado de regalo):
 *      nunca se recalcula contra las ofertas de hoy, porque la oferta original
 *      pudo haber terminado.
 *   3. Descuento de oferta: se recalcula en vivo.
 *   4. Sin nada guardado: se calcula en vivo, lo que incluye paquetes
 *      comprados después de agendar la cita.
 */
export function getAppointmentOfferInfo(
deps: PricingDeps,
appointment: any,
serviceId: string)
: OfferInfo {
  const service = deps.services.find((s: any) => s.id === serviceId);
  const price = service?.price || 0;

  // 1 · Excepción manual para UNA cita puntual.
  const overridePercent =
  appointment.serviceDiscounts?.[`${serviceId}__override`];
  if (overridePercent !== undefined && overridePercent !== null) {
    const discountAmount = price * (Number(overridePercent) / 100);
    return {
      offer: undefined,
      isPackageDiscount: false,
      discountPercent: Number(overridePercent),
      discountAmount,
      finalPrice: Math.max(0, price - discountAmount)
    };
  }

  const storedPercent = appointment.serviceDiscounts?.[serviceId];
  if (storedPercent !== undefined && storedPercent !== null) {
    const patient = deps.getPatient(appointment.patientId);
    const isPackageDiscount = !!patient?.sessionPackages?.some(
      (p: any) =>
      p.serviceId === serviceId &&
      new Date(p.createdAt) <= new Date(appointment.date + 'T23:59:59')
    );
    const isGiftCertificateDiscount = !!appointment.giftCertificateId;

    // 2 · Descuento congelado.
    if (isPackageDiscount || isGiftCertificateDiscount) {
      const discountAmount = price * (Number(storedPercent) / 100);
      return {
        offer: undefined,
        isPackageDiscount,
        discountPercent: Number(storedPercent),
        discountAmount,
        finalPrice: Math.max(0, price - discountAmount)
      };
    }

    // 3 · Oferta: recalcular en vivo.
    return getServiceOfferInfo(
      deps,
      serviceId,
      appointment.date,
      appointment.patientId
    );
  }

  // 4 · Nada guardado.
  return getServiceOfferInfo(
    deps,
    serviceId,
    appointment.date,
    appointment.patientId
  );
}

/** Total real de una cita, ya con descuentos aplicados. */
export function getAppointmentTotal(
deps: PricingDeps,
appointment: any,
serviceIds: string[])
: number {
  return serviceIds.reduce(
    (sum, id) => sum + getAppointmentOfferInfo(deps, appointment, id).finalPrice,
    0
  );
}
